import { useMemo } from "react";

import {
  useFetchAPIError,
  usePrivateAPIData,
} from "contexts/api-data-provider";
import { appointmentSeriesFromApiData } from "./utils";

type AppointmentWithSeriesId = {
  appointment_series_id: string;
};

export function useAppointmentSeries<T extends AppointmentWithSeriesId>({
  appointments,
  reloadTimestamp,
  onBehalfOf,
}: {
  appointments: T[];
  reloadTimestamp: number | undefined;
  onBehalfOf?: string;
}): {
  appointments: (T & { appointment_series?: AppointmentSeriesMetaData })[];
  appointmentSeriesError?: string;
  appointmentSeriesErrorById: { [key: string]: String };
} {
  const appointmentSeriesIds = useMemo(
    () =>
      new Set(
        appointments.map((a) => a.appointment_series_id).filter((a) => !!a),
      ),
    [appointments],
  );

  const appointmentsRequests = useMemo(() => {
    const requests = {};
    for (const requestAppointmentSeriesId of appointmentSeriesIds) {
      requests[`appointmentsSeriesById_(${requestAppointmentSeriesId})`] = {
        method: "POST",
        resource: `/api/scheduler/v1/appointment/series/snapshot${
          onBehalfOf ? `?on_behalf_of=${onBehalfOf}` : ""
        }`,
        fetchArgs: {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            appointment_series_ids: [requestAppointmentSeriesId],
          }),
        },
      };
    }
    return requests;
  }, [appointmentSeriesIds, onBehalfOf]);

  const appointmentSeriesResponses = usePrivateAPIData(
    appointmentsRequests,
    reloadTimestamp,
  );

  const appointmentSeriesById = useMemo(() => {
    const appointmentSeriesById = {};
    const appointmentSeries = Object.values(appointmentSeriesResponses)
      .filter((resp) => resp.result)
      .map((resp) =>
        resp.result.appointment_series
          .map((d) => appointmentSeriesFromApiData(d))
          .flat(),
      )
      .flat();
    for (const series of appointmentSeries) {
      appointmentSeriesById[series.appointment_series_id] = series;
    }
    return appointmentSeriesById;
  }, [appointmentSeriesResponses]);

  const appointmentSeriesError = useFetchAPIError(appointmentSeriesResponses);

  const appointmentsMerged = useMemo(() => {
    return appointments.map((a) => {
      return {
        ...a,
        appointment_series: appointmentSeriesById[a.appointment_series_id],
      };
    });
  }, [appointments, appointmentSeriesById]);

  const appointmentSeriesErrorById = useMemo(() => {
    const appointmentsErrorById = {};
    for (const resp of Object.values(appointmentSeriesResponses)) {
      if (!resp.result) continue;
      for (const appErr of resp.result.errors) {
        appointmentsErrorById[appErr.appointment_id] = appErr.error;
      }
    }
    return appointmentsErrorById;
  }, [appointmentSeriesResponses]);

  return {
    appointments: appointmentsMerged,
    appointmentSeriesError,
    appointmentSeriesErrorById,
  };
}
